import { BusFill18Icon } from "@vygruppen/spor-icon-react";
import { BussesPassengersCounter } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/MeasureProgress/components/BussesPassengersCounter";
import { CAPACITY_PER_BUS } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/constants";
import { AltTransStretchCoverage } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/types";
import { FC } from "react";
import {
  Badge,
  Box,
  DropsButton,
  Flex,
  Grid,
  Text,
  tokens,
} from "shared/components/Spor";
import { Semantics } from "shared/theme/sidespor/theme/themeSemantics";
import { useTheme } from "styled-components";

type BusBadgeProps = {
  gridColumn: string;
  semantic: Semantics;
  value: number;
};

const BusBadge: FC<BusBadgeProps> = ({ gridColumn, semantic, value }) => (
  <Badge
    semantic={semantic}
    minWidth={7}
    gridColumn={gridColumn}
    textAlign="start"
    height={4}
    borderRadius={0}
    _first={{ borderLeftRadius: "3px" }}
    _last={{ borderRightRadius: "3px" }}
  >
    <BusFill18Icon />
    <Text variant="xs" mr="auto">
      {value}
    </Text>
  </Badge>
);

interface StretchProgressCardProps {
  stretchCoverage: AltTransStretchCoverage;
  selectedStretch: AltTransStretchCoverage | null;
  setStretch: (stretch: AltTransStretchCoverage | null) => void;
}

export const StretchProgressCard: FC<StretchProgressCardProps> = ({
  stretchCoverage,
  selectedStretch,
  setStretch,
}) => {
  const theme = useTheme();

  const { totalStretchCoverage, maxPassengers, stretchName } = stretchCoverage;
  const {
    stillRequired: _stillRequired,
    totalOrdered,
    totalConfirmed,
    totalOperational,
    totalRequired,
  } = totalStretchCoverage;
  const stillRequired = _stillRequired ?? 0;

  const isSelected = stretchName === selectedStretch?.stretchName;

  const busesRequired =
    totalRequired ?? Math.ceil(maxPassengers / CAPACITY_PER_BUS);

  const noBuses =
    stillRequired + totalOrdered + totalConfirmed + totalOperational === 0;

  const gridTemplateColumns = `[stillRequired] ${stillRequired}fr [totalOrdered] ${totalOrdered}fr [totalConfirmed] ${totalConfirmed}fr [totalOperational] ${totalOperational}fr ${noBuses ? "[empty] 1fr" : ""}`;

  return (
    <DropsButton
      variant="ghost"
      gap={0.5}
      alignItems="center"
      flexDirection="column"
      border="1px solid"
      py={1.5}
      px={2}
      borderRadius={tokens.default.size.spacing[1]}
      borderColor={theme.colorOutlineSecondary}
      width="100%"
      onClick={() => setStretch(isSelected ? null : stretchCoverage)}
      backgroundColor={
        isSelected ? theme.colorSelected : theme.colorBackgroundSecondary
      }
    >
      <Flex
        alignItems="center"
        justifyContent="space-between"
        width="100%"
        height="27px"
        mt="-2px"
        fontWeight="normal"
      >
        <Text color={theme.colorTextMain} variant="xs">
          {stretchName}
        </Text>
        <BussesPassengersCounter
          vehicles={busesRequired}
          passengers={maxPassengers}
          vehiclesFromMeasureCard={
            totalStretchCoverage.totalRequiredByMeasureCard
          }
        />
      </Flex>

      <Grid gridTemplateColumns={gridTemplateColumns} width="100%" minWidth={7}>
        {noBuses ? (
          <Box
            borderRadius="2px"
            textAlign="start"
            backgroundColor={theme.colorBackgroundMain}
            height={4}
            gridColumn="empty"
          />
        ) : (
          <>
            {stillRequired > 0 && (
              <BusBadge
                gridColumn="stillRequired"
                semantic="alarm"
                value={stillRequired}
              />
            )}
            {totalOrdered > 0 && (
              <BusBadge
                gridColumn="totalOrdered"
                semantic="warning"
                value={totalOrdered}
              />
            )}
            {totalConfirmed > 0 && (
              <BusBadge
                gridColumn="totalConfirmed"
                semantic="info"
                value={totalConfirmed}
              />
            )}
            {totalOperational > 0 && (
              <BusBadge
                gridColumn="totalOperational"
                semantic="success"
                value={totalOperational}
              />
            )}
          </>
        )}
      </Grid>
    </DropsButton>
  );
};
